import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle, Sparkles } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const TIERS = [
  {
    code: "starter",
    name: "Starter",
    premium: 99,
    cover: 50000,
    bestFor: "Mama-mboga, kibanda owners, market vendors, small kiosk operators",
    perks: [
      "M-Pesa reversal fraud",
      "Phishing & account takeover loss",
      "WhatsApp claim filing",
      "Standard FlashClaim queue",
      "Cover up to KES 50,000 per claim",
    ],
  },
  {
    code: "growth",
    name: "Growth",
    premium: 249,
    cover: 250000,
    featured: true,
    bestFor: "Online sellers, Instagram shops, WhatsApp catalogue merchants, social-commerce hustlers",
    perks: [
      "Everything in Starter",
      "Fake-customer dispute cover",
      "Social-commerce scam cover",
      "Priority FlashClaim queue",
      "Cover up to KES 250,000 per claim",
    ],
  },
  {
    code: "business",
    name: "Business",
    premium: 499,
    cover: 1000000,
    bestFor: "POS agents, multi-till operators, growing SMEs, micro-enterprises with staff",
    perks: [
      "Everything in Growth",
      "POS settlement-loss cover",
      "Delivery / payment mismatch",
      "Dedicated SIU concierge",
      "Cover up to KES 1,000,000 per claim",
    ],
  },
];

export default function CoverTiers() {
  usePageMeta({
    title: "Cover tiers — Fortiqa",
    description:
      "Starter, Growth and Business cyber-cover for Kenyan merchants. From KES 99 / week. Pause or upgrade anytime.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="cover-tiers-page">
      <MarketingPageShell
        eyebrow="Cover tiers"
        title="Cover starts at KES 99 / week."
        intro="Three tiers, one platform. Weekly premiums via M-Pesa, seven-day cooling-off on first activation, and you can pause or upgrade anytime."
        testid="cover-tiers"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 grid lg:grid-cols-3 gap-5 lg:gap-6">
          {TIERS.map((t) => (
            <div
              key={t.code}
              className={`relative rounded-3xl p-7 lg:p-8 flex flex-col transition-all hover:shadow-xl ${
                t.featured
                  ? "bg-[#114232] text-[#f4f2eb] shadow-lg lg:-translate-y-4"
                  : "bg-white border border-[#114232]/10 hover:border-[#114232]/30"
              }`}
              data-testid={`cover-tier-${t.code}`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#c15337] text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                  <Sparkles className="w-3 h-3" /> Most popular
                </div>
              )}
              <div
                className={`text-[10px] uppercase tracking-[0.22em] font-bold mb-2 ${
                  t.featured ? "text-[#d49a36]" : "text-[#c15337]"
                }`}
              >
                {t.name}
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-heading text-4xl sm:text-5xl font-extrabold">
                  KES {t.premium}
                </span>
                <span className={`text-sm ${t.featured ? "text-[#f4f2eb]/70" : "text-[#5c6360]"}`}>
                  /week
                </span>
              </div>
              <div className={`text-xs mb-5 ${t.featured ? "text-[#f4f2eb]/75" : "text-[#5c6360]"}`}>
                {t.bestFor}
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className={`w-4 h-4 mt-0.5 shrink-0 ${
                        t.featured ? "text-[#d49a36]" : "text-[#2d7a4a]"
                      }`}
                      strokeWidth={3}
                    />
                    <span className={t.featured ? "text-[#f4f2eb]/95" : "text-[#1b1d1c]"}>
                      {p}
                    </span>
                  </li>
                ))}
              </ul>
              <a
                href={`https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20sign%20me%20up%20for%20${encodeURIComponent(t.name)}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`cover-tier-cta-${t.code}`}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-all ${
                  t.featured
                    ? "bg-[#c15337] hover:bg-[#a8472f] text-white shadow-md hover:shadow-lg"
                    : "bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] shadow-sm hover:shadow-md"
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                Sign up via WhatsApp
              </a>
            </div>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Not sure which tier?
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Talk to our team — 60-second WhatsApp triage.
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
              data-testid="cover-tiers-talk"
            >
              Contact us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
